import React from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  EPDs_PAGE_SIZE,
  epdSearchStore,
} from "features/EPDSearchModal/EPDSearchStore";
import {
  EPDSearchOUT,
  Material2050SearchOUT,
  PagedEPDSearchOUT,
  PaginatedMaterial2050SearchOUT,
} from "api-client";
import { observer } from "mobx-react-lite";
import LoadingView from "../components/LoadingView";
import EmptyView from "../components/EmptyView";
import EPDOverview from "./EPDOverview";
import SearchRow from "../components/SearchRow";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { Button } from "@material-ui/core";

export interface OverviewProps {
  state: boolean;
  epd: Material2050SearchOUT | EPDSearchOUT | null;
}

export interface LoadingState {
  id: string;
  state: boolean;
}

const SearchTable = (props: {
  term: string;
  datasetIndex?: number;
  rowIndex?: number;
  close: () => void;
  generate?: boolean;
  createEPDButton?: () => React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [showOverview, setShowOverview] = React.useState<OverviewProps>({
    state: false,
    epd: null,
  });

  const [hasMoreOptions, setHasMoreOptions] = React.useState(false);
  const [loadingEPDs, setLoadingEPDs] = React.useState(true);
  const [loadingEPDSelect, setLoadingEPDSelect] = React.useState<LoadingState>({
    id: "",
    state: false,
  });
  const [error, setError] = React.useState<number>();
  const EPDItems = epdSearchStore.EPDs?.items;

  React.useEffect(() => {
    if (!sidebarMenuStore.currentOrganisation?.id) return;
    (async () => {
      await getEPDs(1, true);
      setShowOverview({ state: false, epd: null });
    })();
  }, [
    props.term,
    epdSearchStore.selectedFilters,
    epdSearchStore.isExternalSearch,
    sidebarMenuStore.currentOrganisation,
  ]);

  async function fetchEPDs(term: string, pageNumber: number, pageSize: number) {
    if (epdSearchStore.isExternalSearch) {
      return await epdSearchStore.fetch2050EPDs(term, pageNumber, pageSize);
    } else {
      return await epdSearchStore.fetchEPDs(term, pageNumber, pageSize);
    }
  }

  async function getEPDs(pageNumber?: number, update?: boolean) {
    setLoadingEPDs(true);
    if (update) epdSearchStore.setEPDs({ count: 0, items: [] });
    try {
      const response = await fetchEPDs(
        props.term,
        pageNumber ?? 1,
        EPDs_PAGE_SIZE
      );
      if (!response) return;
      if (update || !epdSearchStore.EPDs.items?.length) {
        epdSearchStore.setEPDs(
          epdSearchStore.isExternalSearch
            ? (response as PaginatedMaterial2050SearchOUT)
            : (response as PagedEPDSearchOUT)
        );
      } else if (epdSearchStore.EPDs.items?.length && response.items?.length) {
        const updatedList = {
          count: response.count,
          items: [...epdSearchStore.EPDs.items, ...response.items],
        };
        epdSearchStore.setEPDs(
          epdSearchStore.isExternalSearch
            ? (updatedList as PaginatedMaterial2050SearchOUT)
            : (updatedList as PagedEPDSearchOUT)
        );
      }
      setHasMoreOptions(EPDItems?.length !== response.count);
      setError(undefined);
    } catch (error) {
      if (error && typeof error === "object" && "status" in error) {
        setError((error as { status: number }).status);
      }
    } finally {
      setLoadingEPDs(false);
    }
  }

  async function loadMoreEPDs(e: React.UIEvent<HTMLUListElement, UIEvent>) {
    e.stopPropagation();
    const reachedToEnd = EPDItems?.length === epdSearchStore.EPDs?.count;
    if (!hasMoreOptions || loadingEPDs || reachedToEnd) return;
    if (e.target) {
      const { scrollHeight, scrollTop, clientHeight } =
        e.target as HTMLDivElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        await getEPDs(Number(EPDItems?.length) / EPDs_PAGE_SIZE + 1, false);
      }
    }
  }

  const openOverview = (epd: Material2050SearchOUT | EPDSearchOUT) => {
    setShowOverview({ state: true, epd: epd });
  };

  const onSelectEPD = async (epd: Material2050SearchOUT | EPDSearchOUT) => {
    setLoadingEPDSelect({ id: epd.id, state: true });
    if (props.datasetIndex === undefined || props.rowIndex === undefined)
      return;
    let selected: boolean = false;
    if (epdSearchStore.isExternalSearch) {
      const res = await epdSearchStore.handleSelect2050EPD(
        epd.id,
        props.datasetIndex,
        props.rowIndex
      );
      if (res) selected = true;
    } else {
      BPEStore.onSelectEPDRow(
        epd as EPDSearchOUT,
        props.datasetIndex,
        props.rowIndex
      );

      selected = true;
    }
    setLoadingEPDSelect({ id: "", state: false });
    selected && props.close();
  };

  function tableFooter() {
    return (
      <div
        className={clsx(
          "bg-gray-50 text-gray-500 px-4 py-2 h-8 flex items-center border-t border-gray-300",
          "text-xs font-medium"
        )}
      >
        {t("productEditor.loadedEPDCount", {
          count: EPDItems?.length,
          total: epdSearchStore.EPDs?.count,
        })}
      </div>
    );
  }

  function showErrorMessage(): string | undefined {
    if (epdSearchStore.isOrganisationalSearch) {
      if (error) return t("productEditor.technicalErrorMessage");

      if (props.term) return t("EPDEditor.noEPDMatched");

      return t("EPDEditor.noEPDFound");
    }

    return error
      ? t("productEditor.technicalErrorMessage")
      : t("productEditor.noEPDFound");
  }

  const closeOverview = () => setShowOverview({ state: false, epd: null });

  return (
    <div className="grid grid-cols-2 border border-gray-300 rounded-md mt-2 overflow-hidden">
      <div
        className={clsx("col-span-1", {
          "col-span-2": !showOverview.state,
          "border-r border-gray-300": showOverview.state,
        })}
      >
        {loadingEPDs && EPDItems?.length === 0 ? (
          <LoadingView
            className="!h-20 !w-20"
            message={t("productEditor.EPDWereLoaded")}
            containerClassName={clsx({
              "h-[calc(100vh-180px)]": epdSearchStore.isExternalSearch,
              "h-[calc(100vh-198px)]":
                searchStore.openEPDSearch.state &&
                searchStore.usingOrganisationSearch,
            })}
          />
        ) : (
          <div className="w-full relative">
            <ul
              className={clsx("h-[calc(100vh-270px)] overflow-y-auto", {
                "h-[calc(100vh-322px)]": loadingEPDs && EPDItems?.length,
                "h-[calc(100vh-215px)]": epdSearchStore.isOrganisationalSearch,
                "!h-[calc(100vh-230px)]":
                  searchStore.openEPDSearch.state &&
                  searchStore.usingOrganisationSearch,
              })}
              onScroll={loadMoreEPDs}
            >
              {EPDItems?.length ? (
                EPDItems?.map((item, index) => (
                  <SearchRow
                    epd={item}
                    key={index}
                    showOverview={showOverview}
                    setOpenOverview={openOverview}
                    handleSelectEPD={onSelectEPD}
                    loadingEPDSelect={loadingEPDSelect}
                    generate={props.generate}
                  />
                ))
              ) : (
                <EmptyView
                  errorCode={error}
                  noResult={EPDItems?.length === 0 && !props.term}
                  message={showErrorMessage()}
                  button={
                    epdSearchStore.isOrganisationalSearch && !error
                      ? props.createEPDButton?.()
                      : undefined
                  }
                />
              )}
            </ul>
            {loadingEPDs && EPDItems?.length && (
              <div
                className={clsx(
                  "border-t bottom-8 bg-white z-30 w-full h-[52px] flex justify-center items-center"
                )}
              >
                <LoadingView
                  className="mr-2 !h-4 !w-4"
                  message={t("productEditor.furtherEPDWereLoaded")}
                  containerClassName="!flex-row"
                  messageClassName="!mt-0"
                />
              </div>
            )}
            {epdSearchStore.EPDs.items?.length ? tableFooter() : null}
          </div>
        )}
      </div>
      {showOverview.state && (
        <EPDOverview
          epd={showOverview.epd}
          close={closeOverview}
          handleSelectEPD={onSelectEPD}
          loadingEPDSelect={loadingEPDSelect}
          generate={props.generate}
        />
      )}
    </div>
  );
};

export default observer(SearchTable);
